// Copyright © 2026 Alex Bruce. All rights reserved.
import React, { useState, useEffect, useRef, useMemo } from "react";
import { sGetRaw, sSetRaw } from "../lib/scopedStorage";
import { DARK_THEME, LIGHT_THEME } from "../lib/theme";
import * as Icon from "./OEIcons";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";
export const CB_KEY = "openestimate_conceptual_budgets_v1";

// ── Legacy interfaces (kept for migration) ──
export interface CBuilding { id: string; name: string; groundSF: number; upperSF: number }
export interface CSiteItem { id: string; name: string; low: number; high: number }

// ── New area-group model ──
export interface CSubArea { id: string; name: string; sf: number }
export interface CAreaGroup {
  id: string; name: string;
  subAreas: CSubArea[];
  lowRate: number; highRate: number;
  refProjectId?: string; // optional benchmark reference
}

export interface CBudget {
  id: string; name: string; savedAt: string;
  // Legacy fields (kept for backward compat)
  buildings: CBuilding[]; rates: number[];
  // Current model
  areaGroups?: CAreaGroup[];
  siteItems: CSiteItem[]; notes: string;
}

function loadBudgets(): CBudget[] {
  try { const s = sGetRaw(CB_KEY); return s ? JSON.parse(s) : []; } catch { return []; }
}
function saveBudgets(list: CBudget[]) {
  try { sSetRaw(CB_KEY, JSON.stringify(list)); } catch {}
}

function migrateToAreaGroups(b: CBudget): CBudget {
  if (b.areaGroups && b.areaGroups.length > 0) return b;
  // Migrate legacy buildings → area groups
  if (b.buildings && b.buildings.length > 0) {
    const minRate = b.rates?.length > 0 ? Math.min(...b.rates) : 0;
    const maxRate = b.rates?.length > 0 ? Math.max(...b.rates) : 0;
    const groups: CAreaGroup[] = b.buildings.map(bld => ({
      id: bld.id,
      name: bld.name,
      subAreas: [
        ...(bld.groundSF > 0 ? [{ id: bld.id + "_g", name: "Ground Floor", sf: bld.groundSF }] : []),
        ...(bld.upperSF > 0 ? [{ id: bld.id + "_u", name: "Upper Floors", sf: bld.upperSF }] : []),
      ],
      lowRate: minRate,
      highRate: maxRate,
    }));
    return { ...b, areaGroups: groups };
  }
  return { ...b, areaGroups: [] };
}

const fmt = (n: number) => "$" + Math.round(n).toLocaleString("en-US");

// Compact form for range cells. A conceptual budget is an order-of-magnitude
// figure, so "$900K – $1.2M" is both narrower and more honest than rendering
// it to the dollar. Full precision stays available via title on the cell.
const fmtCompact = (n: number) => {
  const a = Math.abs(n);
  if (a >= 1_000_000) return "$" + (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (a >= 1_000)     return "$" + Math.round(n / 1_000) + "K";
  return "$" + Math.round(n);
};

/**
 * One editable number, formatted with separators when idle and raw while
 * focused, right-aligned and tabular in both states so a column's right edge
 * never moves as you tab through it.
 *
 * Every number in this editor went through a different treatment before:
 * raw `14200` beside formatted `56,900`, a `$` glyph pinned to the left of a
 * cell with its digits right-aligned 130px away, and three different input
 * paddings. One field, used everywhere, is what keeps the columns honest.
 */
function NumField({ T, value, onChange, money, placeholder, ariaLabel, width, bold, padLeft }: {
  T: any; value: number; onChange: (n: number) => void;
  money?: boolean; placeholder?: string; ariaLabel?: string;
  width?: number | string; bold?: boolean; padLeft?: number;
}) {
  const [focused, setFocused] = useState(false);
  const shown = focused ? (value || "") : value > 0 ? (money ? "$" : "") + value.toLocaleString("en-US") : "";
  return (
    <input inputMode="decimal" aria-label={ariaLabel} placeholder={placeholder} value={shown as any}
      onFocus={e => { setFocused(true); e.currentTarget.style.background = T.bgInput; }}
      onBlur={e => { setFocused(false); e.currentTarget.style.background = "none"; }}
      onChange={e => onChange(parseFloat(e.target.value.replace(/[^0-9.]/g, "")) || 0)}
      style={{ background: "none", border: "none", outline: "none", borderRadius: 3,
        width: width ?? "100%", padding: "4px 8px", paddingLeft: padLeft,
        color: T.textPrimary, fontSize: 12, fontFamily: FONT, boxSizing: "border-box",
        textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: bold ? 600 : 400 }} />
  );
}

// ── Benchmark project type ──
interface BenchmarkRef { id: string; name: string; costPerSF: number; total: number; buildingArea: number }

interface Props {
  budgetId: string;
  onBack: () => void;
  storage?: any; // SupabaseStorageProvider for loading benchmark data
}

export default function ConceptualBudgetEditor({ budgetId, onBack, storage }: Props) {
  const [isDarkMode] = useState<boolean>(() => {
    try { const s = sGetRaw("oe_dark_mode"); return s === null ? true : s === "true"; } catch { return true; }
  });
  const T = isDarkMode ? DARK_THEME : LIGHT_THEME;

  const [winW, setWinW] = useState(() => typeof window !== "undefined" ? window.innerWidth : 1440);
  useEffect(() => {
    const onResize = () => setWinW(window.innerWidth);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isMobile = winW < 640;
  const hPad = isMobile ? "16px" : winW < 1024 ? "24px" : "36px";

  const undoStack = useRef<any[]>([]);
  const MAX_UNDO = 40;

  // Load this budget from localStorage (with migration)
  const [budget, setBudget] = useState<CBudget | null>(() => {
    const raw = loadBudgets().find(b => b.id === budgetId) ?? null;
    return raw ? migrateToAreaGroups(raw) : null;
  });
  const [saveStatus, setSaveStatus] = useState<"saved" | "saving">("saved");

  // Load benchmark projects from cloud storage
  const [benchmarkProjects, setBenchmarkProjects] = useState<BenchmarkRef[]>([]);
  useEffect(() => {
    if (!storage?.listProjectsAsync) return;
    storage.listProjectsAsync().then((list: any[]) => {
      setBenchmarkProjects(
        list.map((p: any) => ({
          id: p.id, name: p.name, costPerSF: Math.round(p.costPerSF || 0), total: p.total || 0, buildingArea: p.buildingArea || 0,
        }))
      );
    }).catch(() => {});
  }, [storage]);

  // Reference modal state
  const [refModal, setRefModal] = useState<{ groupId: string } | null>(null);
  const [refExpanded, setRefExpanded] = useState<string | null>(null); // expanded project ID
  const [refProjectDetail, setRefProjectDetail] = useState<any>(null);
  const [refLoading, setRefLoading] = useState(false);

  const loadProjectDetail = async (projectId: string) => {
    if (!storage?.loadProjectById) return;
    setRefLoading(true);
    setRefProjectDetail(null);
    try {
      const state = await storage.loadProjectById(projectId);
      if (state) setRefProjectDetail(state);
    } catch {}
    setRefLoading(false);
  };

  const toggleRefProject = (projectId: string) => {
    if (refExpanded === projectId) { setRefExpanded(null); setRefProjectDetail(null); }
    else { setRefExpanded(projectId); loadProjectDetail(projectId); }
  };

  const applyRefRate = (costPerSF: number) => {
    if (!refModal) return;
    const low = Math.round(costPerSF * 0.9);
    const high = Math.round(costPerSF * 1.1);
    updGroup(refModal.groupId, { lowRate: low, highRate: high, refProjectId: refExpanded || undefined });
    setRefModal(null); setRefExpanded(null); setRefProjectDetail(null);
  };

  // Auto-save whenever budget changes
  useEffect(() => {
    if (!budget) return;
    setSaveStatus("saving");
    const t = setTimeout(() => {
      const all = loadBudgets();
      const updated = all.some(b => b.id === budget.id)
        ? all.map(b => b.id === budget.id ? budget : b)
        : [...all, budget];
      saveBudgets(updated);
      setSaveStatus("saved");
    }, 600);
    return () => clearTimeout(t);
  }, [budget]);

  const pushUndo = () => {
    undoStack.current = [...undoStack.current.slice(-(MAX_UNDO - 1)), JSON.parse(JSON.stringify(budget))];
  };

  const undo = () => {
    if (undoStack.current.length === 0) return;
    const prev = undoStack.current.pop();
    setBudget(prev);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "z") { e.preventDefault(); undo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const upd = (patch: Partial<CBudget>) => {
    pushUndo();
    setBudget(prev => prev ? { ...prev, ...patch, savedAt: new Date().toISOString() } : prev);
  };

  const updGroup = (groupId: string, patch: Partial<CAreaGroup>) => {
    if (!budget?.areaGroups) return;
    upd({ areaGroups: budget.areaGroups.map(g => g.id === groupId ? { ...g, ...patch } : g) });
  };

  if (!budget) {
    return (
      <div style={{ fontFamily: FONT, background: T.bgApp, color: T.textPrimary, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 14, color: T.textMuted, marginBottom: 12 }}>Budget not found.</div>
          <button onClick={onBack} style={{ background: T.bgAccentSubtle, border: `1px solid ${T.borderAccent}55`, color: T.borderAccent, cursor: "pointer", padding: "8px 20px", borderRadius: 3, fontSize: 12, fontFamily: FONT, display: "inline-flex", alignItems: "center", gap: 4 }}><Icon.ArrowLeft size={14}/> Back</button>
        </div>
      </div>
    );
  }

  const groups = budget.areaGroups || [];
  const site = budget.siteItems;
  const siteLow = site.reduce((s, i) => (i.low || 0) + s, 0);
  const siteHigh = site.reduce((s, i) => (i.high || 0) + s, 0);

  // Compute per-group totals — always normalize so low ≤ high regardless of entry order
  const groupCalcs = groups.map(g => {
    const totalSF = g.subAreas.reduce((s, a) => (a.sf || 0) + s, 0);
    const a = totalSF * (g.lowRate || 0);
    const b = totalSF * (g.highRate || 0);
    return { totalSF, low: Math.min(a, b), high: Math.max(a, b) };
  });
  const grandTotalSF = groupCalcs.reduce((s, c) => s + c.totalSF, 0);
  const siteLowN = Math.min(siteLow, siteHigh);
  const siteHighN = Math.max(siteLow, siteHigh);
  const grandLow = groupCalcs.reduce((s, c) => s + c.low, 0) + siteLowN;
  const grandHigh = groupCalcs.reduce((s, c) => s + c.high, 0) + siteHighN;

  // ── Table vocabulary, borrowed wholesale from the Estimate tab ───────
  // No vertical rules, one hairline between rows, uppercase micro column
  // labels, numbers right-aligned and tabular. This screen used to box every
  // cell in a full border, mix three border weights, and fill its headers with
  // a hard-coded teal on one table and a hard-coded purple on the other --
  // which is why it read as a different product bolted on to the side.
  //
  // overflow:hidden pairs with the table's fixed layout: columns are locked by
  // the colgroup, so cell content wider than its column would otherwise push
  // the wrapper's scrollWidth and bring back the horizontal scrollbar.
  const ROW_RULE = `1px solid ${T.borderSecondary}`;
  const cell: React.CSSProperties = { padding: "4px 8px", verticalAlign: "middle", overflow: "hidden" };
  const num: React.CSSProperties = { ...cell, textAlign: "right", fontVariantNumeric: "tabular-nums" };
  const hdr: React.CSSProperties = { padding: "5px 8px", textAlign: "left", fontSize: 10, color: T.textMuted,
    letterSpacing: ".1em", fontWeight: 500, textTransform: "uppercase", whiteSpace: "nowrap" };
  const hdrNum: React.CSSProperties = { ...hdr, textAlign: "right" };
  // Totals are opaque on purpose: the building subtotal is sticky, and the
  // accent tints in this theme carry alpha, so a tinted sticky row would let
  // the rows it covers bleed through it.
  const totCell: React.CSSProperties = { ...cell, padding: "8px", fontWeight: 700, color: T.borderAccent,
    background: T.bgPanel, borderTop: `2px solid ${T.borderAccent}55` };
  const totNum: React.CSSProperties = { ...totCell, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
  const stick: React.CSSProperties = { position: "sticky", bottom: 0, zIndex: 2 };
  const inp: React.CSSProperties = { background: "none", border: "none", outline: "none", padding: "4px 8px",
    color: T.textPrimary, fontSize: 12, fontFamily: FONT, boxSizing: "border-box", width: "100%" };
  const iconBtn: React.CSSProperties = { background: "none", border: "none", color: T.textWeak, cursor: "pointer",
    padding: "2px 4px", fontFamily: FONT, lineHeight: 1 };
  const chipBtn: React.CSSProperties = { fontSize: 10, padding: "2px 8px", background: "none",
    border: `1px solid ${T.borderSecondary}`, color: T.textMuted, borderRadius: 3, cursor: "pointer",
    fontFamily: FONT, display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap", flexShrink: 0 };
  const addBtn: React.CSSProperties = { background: T.bgAccentSubtle, border: `1px solid ${T.borderAccent}55`,
    color: T.borderAccent, cursor: "pointer", padding: "6px 14px", borderRadius: 3, fontSize: 11,
    fontFamily: FONT, fontWeight: 600, flexShrink: 0 };

  const addGroup = () => {
    const id = "ag" + Date.now();
    upd({ areaGroups: [...groups, { id, name: "New Area", subAreas: [{ id: id + "_1", name: "Area 1", sf: 0 }], lowRate: 0, highRate: 0 }] });
  };

  const removeGroup = (gid: string) => {
    upd({ areaGroups: groups.filter(g => g.id !== gid) });
  };

  // This used to ask first, on the stated grounds that the editor had no undo
  // stack. It has had one the whole time -- removeGroup goes through upd(),
  // which pushes a snapshot, and the toolbar carries a live Undo button next
  // to Ctrl+Z. The dialog was guarding against nothing, and telling the user
  // their work was unrecoverable when it was one click away.
  const confirmRemoveGroup = (g: any) => removeGroup(g.id);

  const addSubArea = (gid: string) => {
    const g = groups.find(x => x.id === gid);
    if (!g) return;
    const sid = gid + "_" + Date.now();
    updGroup(gid, { subAreas: [...g.subAreas, { id: sid, name: "", sf: 0 }] });
  };

  const updSubArea = (gid: string, sid: string, patch: Partial<CSubArea>) => {
    const g = groups.find(x => x.id === gid);
    if (!g) return;
    updGroup(gid, { subAreas: g.subAreas.map(a => a.id === sid ? { ...a, ...patch } : a) });
  };

  const removeSubArea = (gid: string, sid: string) => {
    const g = groups.find(x => x.id === gid);
    if (!g) return;
    updGroup(gid, { subAreas: g.subAreas.filter(a => a.id !== sid) });
  };

  return (
    <div style={{ fontFamily: FONT, background: T.bgApp, color: T.textPrimary, minHeight: "100vh", display: "flex", flexDirection: "column" }}>

      {/* ── Top bar ── */}
      <div style={{ background: T.bgHeader, borderBottom: `1px solid ${T.borderPrimary}`, padding: `10px ${hPad}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          <button onClick={onBack} title="Back to projects"
            style={{ background: "none", border: `1px solid ${T.textInverse}44`, color: T.textInverse, cursor: "pointer", padding: "5px 12px", borderRadius: 3, fontSize: 12, fontFamily: FONT, lineHeight: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
            onMouseEnter={e => e.currentTarget.style.borderColor = T.borderAccent}
            onMouseLeave={e => e.currentTarget.style.borderColor = `${T.textInverse}44`}>
            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor"><path d="M10 1L1 9h3v8h5v-5h2v5h5V9h3L10 1z" /></svg>
          </button>
          <input
            value={budget.name}
            onChange={e => upd({ name: e.target.value })}
            style={{ background: "none", border: "none", fontSize: isMobile ? 14 : 16, fontWeight: 700, color: T.textInverse, fontFamily: FONT, outline: "none", minWidth: 0, maxWidth: isMobile ? 160 : 340 }}
          />
          <span style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".08em", flexShrink: 0 }}>
            CONCEPTUAL BUDGET
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <button onClick={undo} disabled={undoStack.current.length === 0}
            title="Undo (Ctrl+Z)"
            style={{ background: "none", border: "none", color: undoStack.current.length > 0 ? T.textMuted : T.textWeak, cursor: undoStack.current.length > 0 ? "pointer" : "default", fontSize: 13, padding: "4px 8px", fontFamily: FONT, opacity: undoStack.current.length > 0 ? 1 : 0.4 }}>
            ↩
          </button>
          <div style={{ fontSize: 11, color: saveStatus === "saved" ? T.positive : T.textMuted, letterSpacing: ".04em" }}>
            {saveStatus === "saved" ? "Saved ✓" : "Saving…"}
          </div>
        </div>
      </div>

      {/* ── Scrollable content ── */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: isMobile ? "20px 16px" : "32px 36px" }}>

          {/* ── Panel: Area Groups ── */}
          <div style={{ background: T.bgPanel, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, padding: "20px 22px", marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 2 }}>Building Program</div>
                <div style={{ fontSize: 11, color: T.textWeak }}>Define area groups with square footage and cost per SF range</div>
              </div>
              <button onClick={addGroup} style={addBtn}>
                + Add Area Group
              </button>
            </div>

            {groups.length === 0 ? (
              <div style={{ border: `1px dashed ${T.borderDashed}`, borderRadius: 6, padding: "24px", textAlign: "center", color: T.textMuted, fontSize: 12 }}>
                Add area groups to define the building program — e.g. Conditioned Space, Covered Porch, Garage, Pool House
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ borderCollapse: "collapse", width: "100%", tableLayout: "fixed", fontSize: 12 }}>
                  {/* Widths sum to 94% plus a fixed action column. Percentages
                      are the only thing holding the numbers on a common right
                      edge, so they live here rather than on individual cells. */}
                  <colgroup>
                    <col style={{ width: "34%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "20%" }} />
                    <col style={{ width: "25%" }} />
                    <col style={{ width: 34 }} />
                  </colgroup>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${T.borderPrimary}` }}>
                      <th style={hdr}>Area Group</th>
                      <th style={hdrNum}>Square Feet</th>
                      {/* A range is one quantity, so it gets one column. */}
                      <th style={hdrNum}>$/SF Range</th>
                      <th style={hdrNum}>Construction</th>
                      <th style={{ ...hdr, padding: "5px 0" }} />
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((g, gi) => {
                      const calc = groupCalcs[gi];
                      const refProject = g.refProjectId ? benchmarkProjects.find(p => p.id === g.refProjectId) : null;
                      return (
                        <React.Fragment key={g.id}>
                          {/* Group header. The rates and the range they produce
                              sit on this line rather than on a trailing
                              "Subtotal:" row: a group's name, area, rate and
                              cost are one statement, and splitting them left
                              four empty bordered cells beside every sub-area. */}
                          <tr style={{ background: `${T.borderAccent}0a`, borderBottom: ROW_RULE,
                            borderTop: gi > 0 ? `2px solid ${T.borderPrimary}` : undefined }}>
                            {/* Line one is the name; line two is every action
                                and reference belonging to the group. The add
                                button used to sit inline after the name, which
                                left it floating in the dead space between the
                                name and the first number column. */}
                            <td style={{ ...cell, padding: "5px 8px", verticalAlign: "top" }}>
                              <input value={g.name} onChange={e => updGroup(g.id, { name: e.target.value })}
                                placeholder="Area group name..."
                                style={{ ...inp, padding: "2px 0", fontSize: 13, fontWeight: 700,
                                  letterSpacing: ".04em", color: T.textSubLabel }} />
                              <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, marginTop: 4 }}>
                                <button onClick={() => addSubArea(g.id)} title="Add a sub-area to this group" style={chipBtn}>
                                  + Sub-area
                                </button>
                                {storage && (
                                  <button onClick={() => { setRefModal({ groupId: g.id }); setRefExpanded(null); setRefProjectDetail(null); }}
                                    title="Use a past project as the reference rate for this area group" style={chipBtn}>
                                    <Icon.BarChart size={12}/> Benchmark
                                  </button>
                                )}
                                {/* Ellipsised, never wrapped: this reference
                                    running to three lines is what used to force
                                    the column wide enough to overflow. */}
                                {refProject && (
                                  <span title={`${refProject.name} · ${fmt(refProject.costPerSF)}/SF`}
                                    style={{ fontSize: 10, color: T.borderAccent, whiteSpace: "nowrap",
                                      overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
                                    {refProject.name} · {fmt(refProject.costPerSF)}/SF
                                  </span>
                                )}
                              </div>
                            </td>
                            {/* The group's figures sit on the name's line, not
                                centred across the two-line cell. */}
                            <td style={{ ...num, fontWeight: 600, color: T.textSecondary, verticalAlign: "top", paddingTop: 7 }}>
                              {calc.totalSF > 0 ? calc.totalSF.toLocaleString() : "—"}
                            </td>
                            <td style={{ ...cell, padding: 0, verticalAlign: "top" }}>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2, paddingTop: 3 }}>
                                <NumField T={T} value={g.lowRate} money bold width={64} placeholder="$0"
                                  ariaLabel="Low cost per square foot"
                                  onChange={n => updGroup(g.id, { lowRate: n })} />
                                <span style={{ color: T.textWeak, flexShrink: 0 }}>–</span>
                                <NumField T={T} value={g.highRate} money bold width={64} placeholder="$0"
                                  ariaLabel="High cost per square foot"
                                  onChange={n => updGroup(g.id, { highRate: n })} />
                              </div>
                            </td>
                            <td title={calc.high > 0 ? `${fmt(calc.low)} – ${fmt(calc.high)}` : undefined}
                              style={{ ...num, fontWeight: 600, color: T.textSecondary, whiteSpace: "nowrap", verticalAlign: "top", paddingTop: 7 }}>
                              {calc.high > 0 ? `${fmtCompact(calc.low)} – ${fmtCompact(calc.high)}` : "—"}
                            </td>
                            <td style={{ ...cell, padding: "4px 0", textAlign: "center", verticalAlign: "top" }}>
                              <button onClick={() => confirmRemoveGroup(g)} title="Delete area group" style={iconBtn}><Icon.X size={11}/></button>
                            </td>
                          </tr>
                          {/* Sub-area rows. The indent is padding on the name,
                              not a spacer column: a column would have to be
                              drawn on the total rows too. */}
                          {g.subAreas.map(a => (
                            <tr key={a.id} style={{ borderBottom: ROW_RULE }}>
                              <td style={{ ...cell, padding: 0 }}>
                                <input value={a.name} onChange={e => updSubArea(g.id, a.id, { name: e.target.value })}
                                  placeholder="Sub-area name..."
                                  style={{ ...inp, paddingLeft: 22, color: T.textSecondary }} />
                              </td>
                              <td style={{ ...cell, padding: 0 }}>
                                <NumField T={T} value={a.sf} placeholder="0" ariaLabel={`Square footage, ${a.name || "sub-area"}`}
                                  onChange={n => updSubArea(g.id, a.id, { sf: n })} />
                              </td>
                              <td style={cell} />
                              <td style={cell} />
                              <td style={{ ...cell, padding: "4px 0", textAlign: "center" }}>
                                {g.subAreas.length > 1 && (
                                  <button onClick={() => removeSubArea(g.id, a.id)} title="Delete sub-area" style={iconBtn}><Icon.X size={11}/></button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                    {/* Sticky, so the number you are tuning stays on screen
                        while you edit the rows above it. Named "Building
                        Subtotal" rather than "Project Total" because it
                        excludes site allowances; the panel below is the project
                        total, and two different numbers must not share a label. */}
                    <tr>
                      <td style={{ ...totCell, ...stick, fontSize: 11, letterSpacing: ".1em" }}>BUILDING SUBTOTAL</td>
                      <td style={{ ...totNum, ...stick }}>{grandTotalSF > 0 ? grandTotalSF.toLocaleString() : "—"}</td>
                      <td style={{ ...totCell, ...stick }} />
                      <td title={grandHigh - siteHighN > 0 ? `${fmt(grandLow - siteLowN)} – ${fmt(grandHigh - siteHighN)}` : undefined}
                        style={{ ...totNum, ...stick }}>
                        {grandHigh - siteHighN > 0 ? `${fmtCompact(grandLow - siteLowN)} – ${fmtCompact(grandHigh - siteHighN)}` : "—"}
                      </td>
                      <td style={{ ...totCell, ...stick, padding: "8px 0" }} />
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Panel: Site Work ── */}
          <div style={{ background: T.bgPanel, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, padding: "20px 22px", marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 2 }}>Site Work / Other Allowances</div>
                <div style={{ fontSize: 11, color: T.textWeak }}>Landscaping, FF&E, utilities, or any off-building costs</div>
              </div>
              <button onClick={() => upd({ siteItems: [...site, { id: "s" + Date.now(), name: "Site Work", low: 0, high: 0 }] })}
                style={addBtn}>
                + Add Line
              </button>
            </div>
            {site.length === 0 ? (
              <div style={{ border: `1px dashed ${T.borderDashed}`, borderRadius: 6, padding: "20px", textAlign: "center", color: T.textMuted, fontSize: 12 }}>
                No site allowances added yet
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                {/* Same vocabulary as the Building Program table above, down to
                    the fixed layout and the 34px action column, so the money in
                    both tables lands on the same right-hand axis. The two used
                    to differ in header colour, border weight, column sizing and
                    number formatting. */}
                <table style={{ borderCollapse: "collapse", width: "100%", tableLayout: "fixed", fontSize: 12 }}>
                  <colgroup>
                    <col style={{ width: "55%" }} />
                    <col style={{ width: "41%" }} />
                    <col style={{ width: 34 }} />
                  </colgroup>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${T.borderPrimary}` }}>
                      <th style={hdr}>Description</th>
                      <th style={hdrNum}>Cost Range</th>
                      <th style={{ ...hdr, padding: "5px 0" }} />
                    </tr>
                  </thead>
                  <tbody>
                    {site.map((si, idx) => (
                      <tr key={si.id} style={{ borderBottom: ROW_RULE }}>
                        <td style={{ ...cell, padding: 0 }}>
                          <input value={si.name} placeholder="Description..."
                            onChange={e => upd({ siteItems: site.map((x, i) => i === idx ? { ...x, name: e.target.value } : x) })}
                            style={inp} />
                        </td>
                        <td style={{ ...cell, padding: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
                            <NumField T={T} value={si.low} money width={120} placeholder="$0" ariaLabel="Low allowance"
                              onChange={n => upd({ siteItems: site.map((x, i) => i === idx ? { ...x, low: n } : x) })} />
                            <span style={{ color: T.textWeak, flexShrink: 0 }}>–</span>
                            <NumField T={T} value={si.high} money width={120} placeholder="$0" ariaLabel="High allowance"
                              onChange={n => upd({ siteItems: site.map((x, i) => i === idx ? { ...x, high: n } : x) })} />
                          </div>
                        </td>
                        <td style={{ ...cell, padding: "4px 0", textAlign: "center" }}>
                          <button onClick={() => upd({ siteItems: site.filter((_, i) => i !== idx) })}
                            title="Delete line" style={iconBtn}><Icon.X size={11}/></button>
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td style={{ ...totCell, fontSize: 11, letterSpacing: ".1em" }}>SITE SUBTOTAL</td>
                      <td style={totNum}>
                        {siteHighN > 0 ? `${fmt(siteLowN)} – ${fmt(siteHighN)}` : "—"}
                      </td>
                      <td style={{ ...totCell, padding: "8px 0" }} />
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Grand Total Summary ── */}
          {(groups.length > 0 || site.length > 0) && (
            <div style={{ background: T.bgPanel, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, padding: "20px 22px", marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 16 }}>Project Total</div>
              {/* The two figures share a baseline and a label style; the meta
                  line sits under them rather than trailing off the end of the
                  same row, where it used to bottom-align against a 32px
                  number and read as a third, unlabelled value. */}
              <div style={{ display: "flex", gap: 28, alignItems: "flex-end", flexWrap: "wrap" }}>
                {([["Low Estimate", grandLow, T.positive], ["High Estimate", grandHigh, T.borderAccent]] as const).map(([label, value, color], i) => (
                  <React.Fragment key={label}>
                    {i > 0 && <div style={{ color: T.textWeak, fontSize: 20, paddingBottom: 4 }}>–</div>}
                    <div>
                      <div style={{ fontSize: 10, color: T.textMuted, letterSpacing: ".1em", textTransform: "uppercase", fontWeight: 500, marginBottom: 6 }}>{label}</div>
                      <div style={{ fontSize: 32, fontWeight: 700, color, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                        {value > 0 ? fmt(value) : "—"}
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
              <div style={{ fontSize: 11, color: T.textWeak, marginTop: 12, fontVariantNumeric: "tabular-nums" }}>
                {grandTotalSF > 0 && <>{grandTotalSF.toLocaleString()} SF · </>}
                {groups.length} area group{groups.length !== 1 ? "s" : ""}
                {site.length > 0 && <> · {site.length} site item{site.length !== 1 ? "s" : ""}</>}
                {grandTotalSF > 0 && grandHigh > 0 && <> · {fmt(grandLow / grandTotalSF)}–{fmt(grandHigh / grandTotalSF)}/SF all-in</>}
              </div>
            </div>
          )}

          {/* ── Notes ── */}
          <div style={{ background: T.bgPanel, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, padding: "20px 22px" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary, marginBottom: 10 }}>Notes / Assumptions</div>
            <textarea value={budget.notes} onChange={e => upd({ notes: e.target.value })}
              placeholder="Assumptions, exclusions, escalation notes, design basis..."
              style={{ width: "100%", boxSizing: "border-box", background: T.bgInput, border: `1px solid ${T.borderSecondary}`, color: T.textPrimary, fontSize: 12, fontFamily: FONT, padding: "10px 12px", borderRadius: 3, resize: "vertical", minHeight: 88, outline: "none" }} />
          </div>

        </div>
      </div>

      {/* ── Reference Project Modal ── */}
      {refModal && (
        <div onClick={() => { setRefModal(null); setRefExpanded(null); setRefProjectDetail(null); }}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: T.bgPanel, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, width: "90%", maxWidth: 640, maxHeight: "80vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {/* Modal header */}
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.borderSecondary}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.borderAccent }}>Reference Project $/SF</div>
                <div style={{ fontSize: 11, color: T.textWeak, marginTop: 2 }}>Select a project and proposal to auto-fill rates (±10%)</div>
              </div>
              <button onClick={() => { setRefModal(null); setRefExpanded(null); setRefProjectDetail(null); }}
                style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontFamily: FONT }}><Icon.X size={11}/></button>
            </div>
            {/* Modal body */}
            <div style={{ flex: 1, overflowY: "auto", padding: "12px 20px" }}>
              {benchmarkProjects.length === 0 ? (
                <div style={{ padding: "32px 0", textAlign: "center", color: T.textMuted, fontSize: 12 }}>
                  No saved projects with cost data found.
                </div>
              ) : (
                benchmarkProjects.map(p => (
                  <div key={p.id} style={{ marginBottom: 8 }}>
                    {/* Project row */}
                    <div onClick={() => toggleRefProject(p.id)}
                      style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: refExpanded === p.id ? `${T.borderAccent}15` : T.bgInput, border: `1px solid ${refExpanded === p.id ? T.borderAccent + "44" : T.borderSecondary}`, borderRadius: 6, cursor: "pointer" }}>
                      <span style={{ fontSize: 10, color: T.textWeak, flexShrink: 0 }}>{refExpanded === p.id ? "▼" : "▶"}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: T.textPrimary }}>{p.name}</div>
                        <div style={{ fontSize: 10, color: T.textWeak }}>{p.buildingArea > 0 ? p.buildingArea.toLocaleString() + " SF" : "—"}</div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.borderAccent }}>{p.costPerSF > 0 ? `$${p.costPerSF}/SF` : "—"}</div>
                        <div style={{ fontSize: 10, color: T.textWeak }}>{p.total > 0 ? fmt(p.total) : ""}</div>
                      </div>
                      {/* Apply project-level rate */}
                      {p.costPerSF > 0 && (
                        <button onClick={e => { e.stopPropagation(); applyRefRate(p.costPerSF); }}
                          title="Apply project $/SF"
                          style={{ background: T.bgAccentSubtle, border: `1px solid ${T.borderAccent}55`, color: T.borderAccent, cursor: "pointer", padding: "4px 10px", borderRadius: 3, fontSize: 10, fontFamily: FONT, fontWeight: 600, flexShrink: 0 }}>
                          Apply
                        </button>
                      )}
                    </div>
                    {/* Expanded: per-proposal breakdown */}
                    {refExpanded === p.id && (
                      <div style={{ marginTop: 4, marginLeft: 20, borderLeft: `2px solid ${T.borderAccent}33`, paddingLeft: 12 }}>
                        {refLoading ? (
                          <div style={{ padding: "12px 0", fontSize: 11, color: T.textMuted }}>Loading proposals...</div>
                        ) : refProjectDetail ? (
                          (() => {
                            const proposals = refProjectDetail.proposals || [];
                            const itemState = refProjectDetail.itemState || {};
                            const divisions = refProjectDetail.divisions || [];
                            const proposalAreas = refProjectDetail.proposalAreas || {};
                            if (proposals.length === 0) return <div style={{ padding: "8px 0", fontSize: 11, color: T.textMuted }}>No proposals defined in this project.</div>;

                            // Compute per-proposal totals
                            const proposalTotals: Record<string, number> = {};
                            for (const div of divisions) {
                              const subs = (div as any).subcategories || [];
                              for (const sub of subs) {
                                for (const item of (sub.items || [])) {
                                  const li = itemState[item.id];
                                  if (!li || !li.included) continue;
                                  const propId = li.proposal || proposals[0]?.id || "p1";
                                  const tot = (li.qty || 0) * (li.unitCost || 0);
                                  proposalTotals[propId] = (proposalTotals[propId] || 0) + tot;
                                }
                              }
                            }

                            return (
                              <div>
                                <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".08em", padding: "8px 0 4px", fontWeight: 600 }}>PROPOSALS</div>
                                {proposals.map((prop: any) => {
                                  const propTotal = proposalTotals[prop.id] || 0;
                                  const propArea = proposalAreas[prop.id] || refProjectDetail.buildingArea || 0;
                                  const propCostPerSF = propArea > 0 ? Math.round(propTotal / propArea) : 0;
                                  return (
                                    <div key={prop.id}
                                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderBottom: `1px solid ${T.borderSecondary}44` }}>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 11, fontWeight: 500, color: T.textPrimary }}>{prop.name}</div>
                                        <div style={{ fontSize: 10, color: T.textWeak }}>
                                          {propArea > 0 ? propArea.toLocaleString() + " SF" : "No area set"}
                                          {propTotal > 0 ? ` · ${fmt(propTotal)}` : ""}
                                        </div>
                                      </div>
                                      <div style={{ fontSize: 12, fontWeight: 700, color: propCostPerSF > 0 ? T.borderAccent : T.textMuted, flexShrink: 0 }}>
                                        {propCostPerSF > 0 ? `$${propCostPerSF}/SF` : "—"}
                                      </div>
                                      {propCostPerSF > 0 && (
                                        <button onClick={() => applyRefRate(propCostPerSF)}
                                          style={{ background: T.bgAccentSubtle, border: `1px solid ${T.borderAccent}55`, color: T.borderAccent, cursor: "pointer", padding: "3px 8px", borderRadius: 3, fontSize: 10, fontFamily: FONT, fontWeight: 600, flexShrink: 0 }}>
                                          Apply
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()
                        ) : (
                          <div style={{ padding: "8px 0", fontSize: 11, color: T.textMuted }}>Could not load project data.</div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
